import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';

export interface TabItem<Id extends string = string> {
    id: Id;
    label: ReactNode;
    icon: LucideIcon;
    disabled?: boolean;
    disabledTitle?: string;
}

export interface TabsProps<Id extends string = string> {
    items: TabItem<Id>[];
    active: Id;
    onChange: (id: Id) => void;
    // 'md' is the detail-page style; 'sm' the compact style inside the Knowledge pages.
    size?: 'sm' | 'md';
    className?: string;
}

export default function Tabs<Id extends string>({
    items, active, onChange, size = 'md', className = 'mb-6',
}: TabsProps<Id>) {
    const isMd = size === 'md';
    const wrapperCls = `border-b border-softgrey${isMd ? ' overflow-x-auto overflow-y-hidden' : ''}${className ? ` ${className}` : ''}`;
    const navCls = isMd ? 'flex -mb-px min-w-max' : 'flex gap-1';
    const btnPadCls = isMd ? 'px-4 md:px-6 py-3 md:py-4' : 'px-4 py-3';
    return (
        <div className={wrapperCls}>
            <nav className={navCls} aria-label="Tabs">
                {items.map(({ id, label, icon: Icon, disabled, disabledTitle }) => {
                    const isActive = active === id;
                    return (
                        <button
                            key={id}
                            type="button"
                            onClick={() => { if (!disabled) onChange(id); }}
                            disabled={disabled}
                            title={disabled ? disabledTitle : undefined}
                            className={`flex items-center gap-2 ${btnPadCls} text-sm font-medium border-b-2 transition-colors ${
                                isActive
                                    ? 'border-brand text-brand'
                                    : 'border-transparent text-coolslate hover:text-graphite hover:border-softgrey'
                            }${disabled ? ' opacity-40 cursor-not-allowed hover:text-coolslate hover:border-transparent' : ''}`}
                        >
                            <Icon className="w-4 h-4" />
                            {label}
                        </button>
                    );
                })}
            </nav>
        </div>
    );
}
